import {
  AccaoKey,
  CelulaPermissao,
  KeycloakRoleOption,
  LinhaModulo,
} from './permissao.model';

const ACAO_KEYS: Record<string, AccaoKey> = {
  VISUALIZAR: 'VISUALIZAR',
  CRIAR: 'CRIAR',
  EDITAR: 'EDITAR',
  ELIMINAR: 'ELIMINAR',
};

const ACAO_LABELS: Record<string, string> = {
  VISUALIZAR: 'Visualizar',
  CRIAR: 'Criar',
  EDITAR: 'Editar',
  ELIMINAR: 'Eliminar',
  GERIR: 'Gerir',
  EXPORTAR: 'Exportar',
};

/**
 * BUG CORRIGIDO (prompt v2): as chaves reais do KeycloakRole não são
 * consistentes em plural/singular — o backend usa "VISUALIZAR_EFETIVOS"
 * (plural) mas "CRIAR_EFETIVO" / "EDITAR_EFETIVO" / "ELIMINAR_EFETIVO"
 * (singular) para o mesmo módulo. O mesmo padrão repete-se em Postos,
 * Escalas, Equipamentos, Ocorrências, Clientes, Alertas e Utilizadores.
 * Sem mapear as duas formas, 24 das 37 roles caíam incorrectamente no
 * fallback "Outras" (só a forma plural, usada por VISUALIZAR_*, batia
 * certo), o que produzia a lista plana empilhada vista no ecrã em vez da
 * grelha. Mapear ambas as formas explicitamente resolve na origem.
 */
const MODULO_LABELS: Record<string, string> = {
  DASHBOARD: 'Dashboard',
  EFETIVOS: 'Efetivos',
  EFETIVO: 'Efetivos',
  POSTOS: 'Postos',
  POSTO: 'Postos',
  ESCALAS: 'Escalas',
  ESCALA: 'Escalas',
  EQUIPAMENTOS: 'Equipamentos',
  EQUIPAMENTO: 'Equipamentos',
  FINANCEIRO: 'Financeiro',
  OCORRENCIAS: 'Ocorrências',
  OCORRENCIA: 'Ocorrências',
  CLIENTES: 'Clientes',
  CLIENTE: 'Clientes',
  ALERTAS: 'Alertas',
  ALERTA: 'Alertas',
  RELATORIOS: 'Relatórios',
  UTILIZADORES: 'Utilizadores',
  UTILIZADOR: 'Utilizadores',
};

/** Ordem de apresentação dos módulos conhecidos; "Outras" fica sempre no fim. */
const ORDEM_MODULOS = [
  'Dashboard',
  'Efetivos',
  'Postos',
  'Escalas',
  'Equipamentos',
  'Financeiro',
  'Ocorrências',
  'Clientes',
  'Alertas',
  'Relatórios',
  'Utilizadores',
];

function toTitleCase(chave: string): string {
  return chave
    .toLowerCase()
    .split('_')
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
    .join(' ');
}

/**
 * Deriva { valor, chave, label, modulo, ariaLabel } a partir de
 * { "1": "VISUALIZAR_DASHBOARD", ... } devolvido por GET /api/Enum/KeycloakRole.
 * Roles cuja chave não corresponda aos dicionários conhecidos (o backend
 * pode acrescentar roles) caem no fallback "Outras" com o label em title
 * case — nunca são descartadas.
 */
export function derivarOpcoesKeycloakRole(
  record: Record<string, string>,
): KeycloakRoleOption[] {
  return Object.entries(record).map(([key, chave]) => {
    const valor = Number(key);
    const ariaLabel = toTitleCase(chave);
    const separador = chave.indexOf('_');
    const acaoPrefix = separador === -1 ? chave : chave.slice(0, separador);
    const moduloPrefix = separador === -1 ? '' : chave.slice(separador + 1);

    const acaoLabel = ACAO_LABELS[acaoPrefix];
    const moduloLabel = MODULO_LABELS[moduloPrefix];

    if (!acaoLabel || !moduloLabel) {
      return { valor, chave, label: ariaLabel, modulo: 'Outras', ariaLabel };
    }
    return { valor, chave, label: acaoLabel, modulo: moduloLabel, ariaLabel };
  });
}

/**
 * Agrupa as opções derivadas em linhas por módulo (FASE 1.1 do prompt v2).
 * Colunas fixas (Visualizar/Criar/Editar/Eliminar) são posicionais —
 * `linha.celulas.CRIAR`, não um array a iterar — para garantir alinhamento
 * em coluna no template. Tudo o resto (Gerir, Exportar, chaves desconhecidas)
 * cai em `outras`. Construída uma única vez; o template só lê o resultado.
 */
export function construirLinhas(opcoes: KeycloakRoleOption[]): LinhaModulo[] {
  const porModulo = new Map<string, KeycloakRoleOption[]>();
  for (const opt of opcoes) {
    if (!porModulo.has(opt.modulo)) porModulo.set(opt.modulo, []);
    porModulo.get(opt.modulo)!.push(opt);
  }

  const modulosPresentes = [...porModulo.keys()];
  const ordemFinal = [
    ...ORDEM_MODULOS.filter((m) => porModulo.has(m)),
    ...modulosPresentes.filter((m) => !ORDEM_MODULOS.includes(m)),
  ];

  return ordemFinal.map((modulo) => {
    const celulas: Partial<Record<AccaoKey, CelulaPermissao>> = {};
    const outras: LinhaModulo['outras'] = [];
    const roles: number[] = [];

    for (const opt of porModulo.get(modulo)!) {
      const celula: CelulaPermissao = {
        roleEnum: opt.valor,
        chave: opt.chave,
        ariaLabel: opt.ariaLabel,
        checked: false,
      };
      roles.push(opt.valor);

      const accaoKey = ACAO_KEYS[opt.label.toUpperCase()];
      if (accaoKey) {
        celulas[accaoKey] = celula;
      } else {
        outras.push({ ...celula, label: opt.label });
      }
    }

    return { modulo, celulas, outras, roles, checked: false, indeterminate: false };
  });
}

/** Lista plana de todos os roleEnum presentes na matriz — usada pelo toggle "Marcar todas". */
export function todosOsRoleEnums(linhas: LinhaModulo[]): number[] {
  return linhas.flatMap((linha) => linha.roles);
}

/**
 * Lista plana de todas as células (fixas + outras) de todas as linhas —
 * usada para actualizar `.checked` em cada uma sem o template ter de chamar
 * um método por célula.
 */
export function todasAsCelulas(linhas: LinhaModulo[]): CelulaPermissao[] {
  return linhas.flatMap((linha) => [
    ...Object.values(linha.celulas).filter((c): c is CelulaPermissao => !!c),
    ...linha.outras,
  ]);
}
